import { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useBrainStore } from '@/state/brainStore';
import { useSimulationStore } from '@/state/simulationStore';
import { useSessionStore } from '@/state/sessionStore';
import { aud, customerName, simTimeFull } from '@/utils/format';
import { bookingPriceAud } from '@/domain/pricing';
import { ChevronLeft, ChevronRight, Calendar, Wallet, Plus, History } from 'lucide-react';
import { format, addDays, startOfWeek, isSameDay, differenceInCalendarDays } from 'date-fns';
import clsx from 'clsx';
import NewBookingModal from '@/components/NewBookingModal';
import BookingDetailCard from '@/components/BookingDetailCard';
import CalendarFilterBar from '@/components/CalendarFilterBar';
import MiniCalendarPopover from '@/components/MiniCalendarPopover';
import {
  EMPTY_CALENDAR_FILTERS,
  bookingMatchesFilters,
  type CalendarFilters,
} from '@/utils/calendarFilters';
import { bookingTileTone, depositPaidLevel, dayOffTileTone } from '@/utils/statusTone';
import { RESCHEDULE_REASON_LABEL } from '@/utils/bookingSummary';
import { placeBookingFromDeposit } from '@/domain/depositFlow';
import { rescheduleBooking } from '@/domain/rescheduleFlow';
import RescheduleReasonChooser from '@/components/calendar/RescheduleReasonChooser';
import { useUIStore } from '@/state/uiStore';
import { Sparkles } from 'lucide-react';
import DayOffModal from '@/components/DayOffModal';
import SlotActionChooser from '@/components/SlotActionChooser';
import DoubleDayBadge from '@/components/DoubleDayBadge';
import StackedHoldsTile from '@/components/StackedHoldsTile';
import { groupOverbookableHolds, type HoldCluster } from '@/utils/stackedHolds';
import { resolveDayOff } from '@/domain/artistAdmin';
import { countDoubleDaysSinceLastDayOff, sessionsThisWeek, WEEKLY_SESSION_BUDGET } from '@/domain/workload';
import { useMakeSaleDraftStore } from '@/state/makeSaleDraftStore';

const SLOT_HEIGHT_PX = 30;
const SLOT_MIN = 30;
const FIRST_HOUR = 9;
const LAST_HOUR = 21;

function buildHourRows(): string[] {
  const rows: string[] = [];
  for (let h = FIRST_HOUR; h < LAST_HOUR; h += 1) {
    rows.push(`${h.toString().padStart(2, '0')}:00`);
    rows.push(`${h.toString().padStart(2, '0')}:30`);
  }
  return rows;
}

type ArtistLike = ReturnType<typeof useBrainStore.getState>['artists'][string];

/** Full-day placeholder info for an artist on a given day, or null when working. */
function dayOffFor(a: ArtistLike, day: Date) {
  return resolveDayOff(a.availability, day);
}

function sessionLabel(len: string): string {
  return len === 'half-day'
    ? 'Half day'
    : len === 'full-day'
      ? 'Full day'
      : len === 'multi-session'
        ? 'Multi'
        : 'Small';
}

export default function StudioCalendar() {
  const { studioId } = useParams<{ studioId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const focusState = location.state as
    | {
        focusDayIso?: string;
        focusArtistId?: string;
        bookForCustomerId?: string;
        placeBookingDepositId?: string;
        moveBookingId?: string;
        rebookForBookingId?: string;
        rebookArtistId?: string;
        rebookMinDateIso?: string;
      }
    | null;
  const bookForCustomerId = focusState?.bookForCustomerId;
  const placeDepositId = focusState?.placeBookingDepositId;
  const rebookForBookingId = focusState?.rebookForBookingId;
  const rebookMinDateIso = focusState?.rebookMinDateIso;
  const deposits = useBrainStore((s) => s.deposits);
  const placeDeposit = placeDepositId ? deposits[placeDepositId] : undefined;
  const pushNotification = useUIStore((s) => s.pushNotification);
  const studios = useBrainStore((s) => s.studios);
  const artists = useBrainStore((s) => s.artists);
  const bookings = useBrainStore((s) => s.bookings);
  const customers = useBrainStore((s) => s.customers);
  const overrideRequests = useBrainStore((s) => s.overrideRequests);
  const clockIso = useSimulationStore((s) => s.clockIso);
  const persona = useSessionStore((s) => s.persona);

  // Booking ids with a pending no-deposit override — tiles badge as "awaiting
  // approval" so a held-for-approval slot reads differently from an ordinary hold.
  const awaitingApprovalBookingIds = useMemo(
    () =>
      new Set(
        Object.values(overrideRequests)
          .filter((r) => r.status === 'pending')
          .map((r) => r.bookingId),
      ),
    [overrideRequests],
  );

  // When placing a paid deposit that carries a discussed slot, land the agent on
  // that artist/day. Explicit focus state (from a deep link) still wins.
  const groundedSlot = placeDeposit?.discussedSlot;
  // A rebook pick lands on the earliest healed day (2 weeks out) by default.
  const initialDay = focusState?.focusDayIso
    ? new Date(focusState.focusDayIso)
    : rebookMinDateIso
      ? new Date(rebookMinDateIso)
      : groundedSlot
        ? new Date(groundedSlot.startIso)
        : new Date(clockIso);
  const [view, setView] = useState<'day' | 'week'>('day');
  const [pickedDay, setPickedDay] = useState(() => initialDay);
  const [weekStart, setWeekStart] = useState(() => startOfWeek(initialDay, { weekStartsOn: 1 }));
  // Week view focuses a single artist; empty/stale ids fall back to the first
  // rostered artist via `activeWeekArtist` below.
  const [weekArtistId, setWeekArtistId] = useState('');
  const [newBooking, setNewBooking] = useState<{ artistId: string; startIso: string } | null>(null);
  const [slotChoice, setSlotChoice] = useState<{ artistId: string; startIso: string } | null>(null);
  const [dayOff, setDayOff] = useState<{ artistId: string; date: string } | null>(null);
  const [filters, setFilters] = useState<CalendarFilters>(EMPTY_CALENDAR_FILTERS);
  const [detailBookingId, setDetailBookingId] = useState<string | null>(null);
  // A booking sent here from the Edit modal's "Reschedule on calendar" — the tile
  // is highlighted and a hint invites a drag to a new slot.
  const [movingId, setMovingId] = useState<string | null>(focusState?.moveBookingId ?? null);
  // A drag captured on the calendar, awaiting a mandatory reschedule reason
  // before it commits (15 Jul ruling: no reschedule without a reason).
  const [pendingMove, setPendingMove] = useState<{
    bookingId: string;
    artistId: string;
    startIso: string;
  } | null>(null);
  const focusArtistId =
    focusState?.focusArtistId ?? focusState?.rebookArtistId ?? groundedSlot?.artistId;

  // Rebook-pick mode: a slot chosen here is written back to the make-sale draft
  // and returns to the floor. A slot inside the 2-week healing window is held in
  // `pendingRebookSlot` until the manager confirms.
  const rebookBooking = rebookForBookingId ? bookings[rebookForBookingId] : undefined;
  const rebookCustomer = rebookBooking ? customers[rebookBooking.customerId] : undefined;
  const [pendingRebookSlot, setPendingRebookSlot] = useState<{
    artistId: string;
    startIso: string;
  } | null>(null);

  const confirmRebookSlot = (artistId: string, startIso: string) => {
    if (!rebookForBookingId) return;
    useMakeSaleDraftStore.getState().setPickedSlot(rebookForBookingId, startIso, artistId);
    navigate(`/floor/${rebookForBookingId}`);
  };

  useEffect(() => {
    if (focusState?.moveBookingId) setMovingId(focusState.moveBookingId);
  }, [focusState?.moveBookingId]);

  useEffect(() => {
    if (focusState?.focusDayIso) {
      const d = new Date(focusState.focusDayIso);
      setPickedDay(d);
      setWeekStart(startOfWeek(d, { weekStartsOn: 1 }));
      setView('day');
    }
  }, [focusState?.focusDayIso]);

  useEffect(() => {
    if (!focusArtistId) return;
    requestAnimationFrame(() => {
      document
        .querySelector(`[data-testid="day-col-${focusArtistId}"]`)
        ?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    });
  }, [focusArtistId, pickedDay]);

  const studio = studioId ? studios[studioId] : undefined;
  const studioArtists = useMemo(
    () => Object.values(artists).filter((a) => a.studioId === studioId && !a.isApprentice),
    [artists, studioId],
  );

  const activeWeekArtist =
    studioArtists.find((a) => a.id === weekArtistId) ?? studioArtists[0];

  const days = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)),
    [weekStart],
  );

  // Full-day session units this week for the artist in the week view (a double
  // day counts as 2). Display/warning only - shown beside the double-day badge.
  const weekSessionUnits = activeWeekArtist
    ? sessionsThisWeek(activeWeekArtist, Object.values(bookings), days.map((d) => format(d, 'yyyy-MM-dd')))
    : 0;

  const rows = buildHourRows();

  const studioBookings = useMemo(
    () => Object.values(bookings).filter((b) => b.studioId === studioId),
    [bookings, studioId],
  );

  // Apply the calendar filter bar (item 7). Deposit level is derived from the
  // booking's linked deposit so the deposit-state chips + hide-filled work.
  const visibleBookings = useMemo(
    () =>
      studioBookings.filter(
        (b) =>
          // Superseded records are history, not live slots: they never render as
          // an occupying tile (the vacated slot reads free). The reschedule
          // history marker reads them via its own lookup below.
          b.status !== 'superseded' &&
          bookingMatchesFilters(b, depositPaidLevel(deposits[b.depositId ?? '']), filters),
      ),
    [studioBookings, deposits, filters],
  );

  // Kept-original records, surfaced only as faint history ghosts on the calendar
  // (never as live tiles). Reschedules preserve the original visibly (15 Jul).
  const supersededBookings = useMemo(
    () => studioBookings.filter((b) => b.status === 'superseded'),
    [studioBookings],
  );

  const bookingsForArtistOnDay = (artistId: string, day: Date) =>
    visibleBookings.filter(
      (b) => b.artistId === artistId && isSameDay(new Date(b.start), day),
    );

  if (!studio) {
    return <p className="px-10 py-12 text-cream-soft italic">Unknown studio.</p>;
  }

  const dayRev = (date: Date, artistId?: string) => {
    let sum = 0;
    for (const b of studioBookings) {
      if (!isSameDay(new Date(b.start), date)) continue;
      if (artistId && b.artistId !== artistId) continue;
      sum +=
        b.sessionLength === 'multi-session'
          ? 2400
          : b.sessionLength === 'full-day'
            ? 2400
            : b.sessionLength === 'half-day'
              ? 1200
              : 400;
    }
    return sum;
  };

  return (
    <section className="px-6 py-6" data-testid={`studio-calendar-${studio.id}`}>
      <header className="flex items-end justify-between mb-4">
        <div>
          <p className="label-small-caps text-gold-bright">{studio.brand}</p>
          <h1 className="heading-display text-2xl text-cream mt-2">{studio.name} calendar</h1>
          <p className="font-inter text-[10px] text-cream-soft mt-1 uppercase tracking-widest">
            {studioArtists.length} artists rostered · {studioBookings.length} bookings this fortnight
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex border border-gold-dim/40 rounded-full overflow-hidden">
            <button
              type="button"
              className={clsx(
                'px-3 py-1.5 text-xs uppercase tracking-widest',
                view === 'day'
                  ? 'bg-gold/15 text-gold-bright'
                  : 'text-cream-soft hover:text-cream',
              )}
              onClick={() => setView('day')}
              data-testid="calendar-view-day"
            >
              Day
            </button>
            <button
              type="button"
              className={clsx(
                'px-3 py-1.5 text-xs uppercase tracking-widest',
                view === 'week'
                  ? 'bg-gold/15 text-gold-bright'
                  : 'text-cream-soft hover:text-cream',
              )}
              onClick={() => setView('week')}
              data-testid="calendar-view-week"
            >
              Week
            </button>
          </div>
          <button
            type="button"
            className="btn-quiet"
            onClick={() =>
              view === 'day'
                ? setPickedDay((d) => addDays(d, -1))
                : setWeekStart((d) => addDays(d, -7))
            }
          >
            <ChevronLeft size={14} /> {view === 'day' ? 'Prev day' : 'Prev week'}
          </button>
          <MiniCalendarPopover
            value={view === 'day' ? pickedDay : weekStart}
            mode={view}
            todayIso={clockIso}
            label={
              view === 'day'
                ? format(pickedDay, 'EEE d MMM yyyy')
                : `Week of ${format(weekStart, 'd MMM yyyy')}`
            }
            onSelect={(day) => {
              if (view === 'day') setPickedDay(day);
              else setWeekStart(startOfWeek(day, { weekStartsOn: 1 }));
            }}
            testId="calendar-date-picker"
          />
          <button
            type="button"
            className="btn-quiet"
            onClick={() =>
              view === 'day'
                ? setPickedDay((d) => addDays(d, 1))
                : setWeekStart((d) => addDays(d, 7))
            }
          >
            {view === 'day' ? 'Next day' : 'Next week'} <ChevronRight size={14} />
          </button>
          <button
            type="button"
            className="btn-ghost text-xs"
            onClick={() => {
              setPickedDay(new Date(clockIso));
              setWeekStart(startOfWeek(new Date(clockIso), { weekStartsOn: 1 }));
            }}
          >
            <Calendar size={12} /> Today
          </button>
        </div>
      </header>

      {rebookForBookingId && rebookCustomer ? (
        <div
          data-testid="rebook-pick-banner"
          className="rounded-lg border border-gold-bright/50 bg-gold/10 px-4 py-3 mb-4 flex items-center justify-between"
        >
          <p className="font-serif text-cream">
            <span className="label-small-caps text-gold-bright mr-2">Picking rebook slot</span>
            for <span className="font-inter">{customerName(rebookCustomer)}</span> — click an open
            slot with{' '}
            {artists[focusState?.rebookArtistId ?? '']?.firstName ?? 'the artist'}. Sittings under 2
            weeks ask you to confirm the healing window.
          </p>
          <button
            type="button"
            onClick={() => navigate(`/floor/${rebookForBookingId}`)}
            className="btn-quiet text-[11px]"
            data-testid="rebook-pick-cancel"
          >
            Cancel
          </button>
        </div>
      ) : placeDeposit && customers[placeDeposit.customerId] ? (
        <div
          data-testid="place-booking-banner"
          className="rounded-lg border border-mint/60 bg-mint/10 px-4 py-3 mb-4 flex items-center justify-between"
        >
          <p className="font-serif text-cream flex items-center gap-2">
            <Sparkles size={14} className="text-mint" />
            <span className="label-small-caps text-mint mr-2">Deposit paid</span>
            Place{' '}
            <span className="font-inter">{customerName(customers[placeDeposit.customerId]!)}</span>
            &apos;s booking (${placeDeposit.amountAud} held). Click any open slot to confirm.
          </p>
          <button
            type="button"
            onClick={() => navigate(`/customer/${placeDeposit.customerId}`)}
            className="btn-quiet text-[11px]"
            data-testid="place-booking-cancel"
          >
            Back to record
          </button>
        </div>
      ) : bookForCustomerId && customers[bookForCustomerId] ? (
        <div
          data-testid="book-for-banner"
          className="rounded-lg border border-gold-bright/50 bg-gold/10 px-4 py-3 mb-4 flex items-center justify-between"
        >
          <p className="font-serif text-cream">
            <span className="label-small-caps text-gold-bright mr-2">Picking slot</span>
            for <span className="font-inter">{customerName(customers[bookForCustomerId]!)}</span>.
            Click any open slot below to confirm the booking.
          </p>
          <button
            type="button"
            onClick={() => navigate(`/customer/${bookForCustomerId}`)}
            className="btn-quiet text-[11px]"
            data-testid="book-for-cancel"
          >
            Cancel
          </button>
        </div>
      ) : null}

      <div className="flex items-center justify-between gap-4 flex-wrap">
        <CalendarFilterBar
          artists={studioArtists}
          filters={filters}
          onChange={setFilters}
          showArtistFilter={view === 'day'}
        />
        {view === 'week' && activeWeekArtist ? (
          <div className="flex items-center gap-2 flex-wrap">
            <DoubleDayBadge
              count={countDoubleDaysSinceLastDayOff(activeWeekArtist, Object.values(bookings), clockIso)}
              name={activeWeekArtist.displayName}
              prominent
            />
            <span
              data-testid="sessions-budget-chip"
              title="Full-day session units this week (a double day counts as 2). Warning only - comp days stay manual."
              className="inline-flex items-baseline gap-1.5 rounded-full border border-gold-dim/50 bg-ink-black px-3 py-1"
            >
              <span
                className={`font-mono text-sm tabular-nums ${
                  weekSessionUnits > WEEKLY_SESSION_BUDGET ? 'text-blood' : 'text-cream'
                }`}
              >
                {weekSessionUnits} / {WEEKLY_SESSION_BUDGET}
              </span>
              <span className="label-small-caps text-cream-faint">sessions this week</span>
            </span>
          </div>
        ) : null}
      </div>

      {view === 'week' && studioArtists.length > 0 ? (
        <div
          className="flex items-center gap-1.5 flex-wrap mb-4"
          data-testid="week-artist-switcher"
        >
          <span className="font-inter text-[10px] text-cream-soft uppercase tracking-widest mr-1">
            Viewing
          </span>
          {studioArtists.map((a) => (
            <button
              key={a.id}
              type="button"
              onClick={() => setWeekArtistId(a.id)}
              data-testid={`week-artist-${a.id}`}
              aria-pressed={activeWeekArtist?.id === a.id}
              className={clsx(
                'px-3 py-1 rounded-full text-xs transition border',
                activeWeekArtist?.id === a.id
                  ? 'bg-gold/15 text-gold-bright border-gold-bright/60'
                  : 'text-cream-soft border-gold-dim/40 hover:text-cream hover:border-gold-bright/40',
              )}
            >
              {a.displayName}
            </button>
          ))}
        </div>
      ) : null}

      {movingId && bookings[movingId] ? (
        <div
          className="mb-3 flex items-center justify-between gap-3 rounded-md border border-gold-bright/50 bg-gold/10 px-3 py-2"
          data-testid="calendar-move-hint"
        >
          <span className="text-sm text-gold-bright">
            Moving{' '}
            {(() => {
              const cust = customers[bookings[movingId]!.customerId];
              return cust ? customerName(cust) : 'this booking';
            })()}
            {' '}— drag the highlighted booking to a new slot.
          </span>
          <button
            type="button"
            onClick={() => setMovingId(null)}
            className="font-inter text-[10px] uppercase tracking-widest text-cream-soft hover:text-cream"
          >
            Cancel
          </button>
        </div>
      ) : null}

      {view === 'day' ? (
        <DayView
          day={pickedDay}
          artists={studioArtists}
          studioId={studio.id}
          rows={rows}
          bookings={visibleBookings}
          supersededBookings={supersededBookings}
          deposits={deposits}
          customers={customers}
          clockIso={clockIso}
          awaitingApprovalIds={awaitingApprovalBookingIds}
          moveBookingId={movingId}
          onMoveBooking={(bookingId, artistId, startIso) => {
            // Keep-original reschedule: a drag never commits on its own. Capture
            // the intended move and require a reason before it lands.
            setPendingMove({ bookingId, artistId, startIso });
          }}
          onSlot={(artistId, startIso) => {
            if (rebookForBookingId) {
              // Under 2 weeks: hold for a confirm. Otherwise return the slot now.
              if (
                rebookMinDateIso &&
                differenceInCalendarDays(new Date(startIso), new Date(rebookMinDateIso)) < 0
              ) {
                setPendingRebookSlot({ artistId, startIso });
              } else {
                confirmRebookSlot(artistId, startIso);
              }
              return;
            }
            if (placeDeposit) {
              placeBookingFromDeposit({
                depositId: placeDeposit.id,
                artistId,
                startIso,
              });
              const cust = customers[placeDeposit.customerId];
              pushNotification({
                intensity: 'high',
                title: 'Booking placed',
                body: `${cust ? customerName(cust) : placeDeposit.customerId} confirmed with ${artists[artistId]?.firstName ?? artistId}.`,
                ttlMs: 5000,
              });
              navigate(`/customer/${placeDeposit.customerId}`, { replace: true });
              return;
            }
            setSlotChoice({ artistId, startIso });
          }}
          onBookingClick={(bookingId) => setDetailBookingId(bookingId)}
        />
      ) : activeWeekArtist ? (
        <WeekView
          days={days}
          dayRev={dayRev}
          artist={activeWeekArtist}
          rows={rows}
          bookingsForArtistOnDay={bookingsForArtistOnDay}
          deposits={deposits}
          customers={customers}
          clockIso={clockIso}
          awaitingApprovalIds={awaitingApprovalBookingIds}
          onBookingClick={(bookingId) => setDetailBookingId(bookingId)}
          onAddOnDay={(date) => {
            const start = new Date(date);
            start.setHours(11, 0, 0, 0);
            setSlotChoice({ artistId: activeWeekArtist.id, startIso: start.toISOString() });
          }}
        />
      ) : (
        <p className="px-2 py-12 text-cream-soft italic">No artists rostered.</p>
      )}

      <footer className="mt-6 panel p-4 flex flex-wrap gap-x-6 gap-y-2 items-center max-w-5xl">
        <p className="label-small-caps">Deposit</p>
        <span className="inline-flex items-center gap-2 text-xs">
          <span className="h-3 w-3 rounded bg-gold-bright/90" /> Paid in full
        </span>
        <span className="inline-flex items-center gap-2 text-xs">
          <span className="h-3 w-3 rounded bg-gold-bright/40" /> Part-paid
        </span>
        <span className="inline-flex items-center gap-2 text-xs">
          <span className="h-3 w-3 rounded bg-gold-bright/10 border border-gold-bright/30" /> Awaiting
        </span>
        <span className="label-small-caps ml-2">Status</span>
        <span className="inline-flex items-center gap-2 text-xs">
          <span className="h-3 w-3 rounded-full bg-azure" /> Checked in
        </span>
        <span className="inline-flex items-center gap-2 text-xs">
          <span className="h-3 w-3 rounded-full bg-violet" /> In chair
        </span>
        <span className="inline-flex items-center gap-2 text-xs">
          <span className="h-3 w-3 rounded-full bg-mint" /> Completed
        </span>
        <span className="inline-flex items-center gap-2 text-xs">
          <span className="h-3 w-3 rounded-full bg-gold-dim" /> Rescheduled
        </span>
        <span className="font-mono text-xs text-cream-soft ml-auto">
          Sim clock: {simTimeFull(clockIso)}
        </span>
      </footer>

      {pendingRebookSlot ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-ink-black/60 px-4"
          data-testid="rebook-under-window"
        >
          <div className="panel p-6 max-w-md space-y-4">
            <p className="label-small-caps text-blood">Under the 2-week healing window</p>
            <p className="font-serif text-cream-soft">
              {format(new Date(pendingRebookSlot.startIso), 'EEE d MMM')} is sooner than 2 weeks after
              the session. Tattoos need time to heal — book it anyway, or pick a later day.
            </p>
            <div className="flex justify-between">
              <button
                type="button"
                className="btn-quiet text-sm"
                onClick={() => setPendingRebookSlot(null)}
                data-testid="rebook-under-window-cancel"
              >
                Pick a later day
              </button>
              <button
                type="button"
                className="btn-primary text-sm"
                onClick={() => {
                  confirmRebookSlot(pendingRebookSlot.artistId, pendingRebookSlot.startIso);
                  setPendingRebookSlot(null);
                }}
                data-testid="rebook-under-window-confirm"
              >
                Book anyway
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {newBooking ? (
        <NewBookingModal
          open
          studioId={studio.id}
          artistId={newBooking.artistId}
          startIso={newBooking.startIso}
          onClose={() => setNewBooking(null)}
          defaultCustomerId={bookForCustomerId}
        />
      ) : null}

      {slotChoice ? (
        <SlotActionChooser
          artistName={studioArtists.find((a) => a.id === slotChoice.artistId)?.displayName ?? 'Artist'}
          startIso={slotChoice.startIso}
          onNewAppointment={() => {
            setNewBooking({ artistId: slotChoice.artistId, startIso: slotChoice.startIso });
            setSlotChoice(null);
          }}
          onDayOff={() => {
            setDayOff({
              artistId: slotChoice.artistId,
              date: format(new Date(slotChoice.startIso), 'yyyy-MM-dd'),
            });
            setSlotChoice(null);
          }}
          onClose={() => setSlotChoice(null)}
        />
      ) : null}

      {dayOff ? (
        <DayOffModal artistId={dayOff.artistId} date={dayOff.date} onClose={() => setDayOff(null)} />
      ) : null}

      {detailBookingId && bookings[detailBookingId] ? (
        <BookingDetailCard
          booking={bookings[detailBookingId]!}
          customer={customers[bookings[detailBookingId]!.customerId]}
          artist={artists[bookings[detailBookingId]!.artistId]}
          deposit={deposits[bookings[detailBookingId]!.depositId ?? '']}
          onClose={() => setDetailBookingId(null)}
          onNavigateBooking={(id) => setDetailBookingId(id)}
          onViewCustomer={() => {
            const cId = bookings[detailBookingId]!.customerId;
            setDetailBookingId(null);
            navigate(`/customer/${cId}`, {
              state: { fromCalendarStudioId: studio.id, fromCalendarDayIso: pickedDay.toISOString() },
            });
          }}
        />
      ) : null}

      {pendingMove ? (
        <RescheduleReasonChooser
          customerName={
            customers[bookings[pendingMove.bookingId]?.customerId ?? '']
              ? customerName(customers[bookings[pendingMove.bookingId]!.customerId]!)
              : undefined
          }
          fromIso={bookings[pendingMove.bookingId]?.start ?? pendingMove.startIso}
          toIso={pendingMove.startIso}
          onPick={(reason, note) => {
            const cust = customers[bookings[pendingMove.bookingId]?.customerId ?? ''];
            rescheduleBooking({
              bookingId: pendingMove.bookingId,
              reason,
              note,
              pathway: { mode: 'move', artistId: pendingMove.artistId, newStartIso: pendingMove.startIso },
              by: persona ?? 'system',
              at: clockIso,
            });
            pushNotification({
              intensity: 'info',
              title: 'Booking rescheduled',
              body: `${cust ? customerName(cust) : 'Booking'} rescheduled to ${simTimeFull(
                pendingMove.startIso,
              )}. The original slot is kept in history.`,
              ttlMs: 3500,
            });
            setPendingMove(null);
            setMovingId(null);
          }}
          onClose={() => {
            setPendingMove(null);
            setMovingId(null);
          }}
        />
      ) : null}
    </section>
  );
}

type CalBooking = ReturnType<typeof useBrainStore.getState>['bookings'][string];

/** One laid-out item in a day column: a normal booking, or a stacked-holds group. */
type DayCell =
  | { kind: 'booking'; startMs: number; endMs: number; booking: CalBooking }
  | { kind: 'stack'; startMs: number; endMs: number; cluster: HoldCluster };

interface DayViewProps {
  day: Date;
  artists: Array<ReturnType<typeof useBrainStore.getState>['artists'][string]>;
  studioId: string;
  rows: string[];
  bookings: Array<ReturnType<typeof useBrainStore.getState>['bookings'][string]>;
  /** Superseded records for this studio — rendered as faint history ghosts. */
  supersededBookings: Array<ReturnType<typeof useBrainStore.getState>['bookings'][string]>;
  deposits: ReturnType<typeof useBrainStore.getState>['deposits'];
  customers: ReturnType<typeof useBrainStore.getState>['customers'];
  clockIso: string;
  onSlot: (artistId: string, startIso: string) => void;
  onBookingClick: (bookingId: string) => void;
  onMoveBooking: (bookingId: string, artistId: string, startIso: string) => void;
  moveBookingId?: string | null;
  awaitingApprovalIds: Set<string>;
}

function DayView({ day, artists, rows, bookings, supersededBookings, deposits, customers, clockIso, onSlot, onBookingClick, onMoveBooking, moveBookingId, awaitingApprovalIds }: DayViewProps) {
  return (
    <div className="flex">
      <div className="w-14 shrink-0 pt-12">
        {rows.map((r) => (
          <div key={r} className="h-[30px] font-mono text-[10px] text-cream-soft text-right pr-2">
            {r.endsWith(':00') ? r : ''}
          </div>
        ))}
      </div>
      <div className="flex-1 overflow-x-auto">
        <div
          className="grid"
          style={{
            gridTemplateColumns: `repeat(${artists.length}, minmax(140px, 1fr))`,
            gap: '4px',
          }}
        >
          {artists.map((artist) => {
            const myBookings = bookings.filter(
              (b) => b.artistId === artist.id && isSameDay(new Date(b.start), day),
            );
            const mySuperseded = supersededBookings.filter(
              (b) => b.artistId === artist.id && isSameDay(new Date(b.start), day),
            );
            const off = resolveDayOff(artist.availability, day);
            const onShift = off === null;
            // Competing this-week ghost holds (overbookable, overlapping) collapse
            // into ONE stacked cell rather than splitting the column into skinny
            // lanes. Everything else - including a lone hold - lays out as today.
            // Only 2+ overlapping holds cluster; a lone hold stays in `myBookings`
            // and falls through the filter below to render as a normal tile.
            const { clusters } = groupOverbookableHolds(myBookings);
            const clusteredIds = new Set(
              clusters.flatMap((c) => c.holds.map((h) => h.id)),
            );
            const cells: DayCell[] = [
              ...myBookings
                .filter((b) => !clusteredIds.has(b.id))
                .map((booking) => {
                  const startMs = new Date(booking.start).getTime();
                  return {
                    kind: 'booking' as const,
                    startMs,
                    endMs: startMs + booking.durationMin * 60_000,
                    booking,
                  };
                }),
              ...clusters.map((cluster) => ({
                kind: 'stack' as const,
                startMs: cluster.startMs,
                endMs: cluster.endMs,
                cluster,
              })),
            ];
            // Lane-pack the cells so concurrent sittings sit side-by-side; a
            // stacked-holds cell packs as a single item at normal booking width.
            const laneEnds: number[] = [];
            const laidDay = cells
              .slice()
              .sort((a, b) => a.startMs - b.startMs)
              .map((cell) => {
                let lane = laneEnds.findIndex((endAt) => endAt <= cell.startMs);
                if (lane === -1) {
                  laneEnds.push(cell.endMs);
                  lane = laneEnds.length - 1;
                } else {
                  laneEnds[lane] = cell.endMs;
                }
                return { cell, lane };
              });
            const totalLanesDay = Math.max(1, laneEnds.length);
            return (
              <div
                key={artist.id}
                className={clsx(
                  'bg-ink-deep rounded-md border border-gold-dim/15',
                  !onShift ? 'opacity-40' : '',
                )}
                data-testid={`day-col-${artist.id}`}
              >
                <div className="text-center py-2 border-b border-gold-dim/15">
                  <p className="font-inter text-sm text-cream">{artist.displayName}</p>
                  {onShift ? (
                    <p className="font-inter text-[10px] text-cream-soft uppercase tracking-widest mt-0.5">
                      {myBookings.length} {myBookings.length === 1 ? 'booking' : 'bookings'}
                    </p>
                  ) : (
                    <p className="font-inter text-[10px] text-blood uppercase tracking-widest mt-0.5">
                      {off ? dayOffTileTone(off.kind).label : 'RDO'}
                    </p>
                  )}
                </div>
                <div
                  className="relative overflow-hidden"
                  style={{ height: `${rows.length * SLOT_HEIGHT_PX}px` }}
                >
                  {rows.map((row, i) => {
                    const [hh, mm] = row.split(':').map((n) => parseInt(n, 10));
                    const slotDate = new Date(day);
                    slotDate.setHours(hh ?? 10, mm ?? 0, 0, 0);
                    const conflict = myBookings.some((b) => {
                      const bs = new Date(b.start).getTime();
                      const be = bs + b.durationMin * 60 * 1000;
                      const ts = slotDate.getTime();
                      return ts >= bs && ts < be;
                    });
                    return (
                      <button
                        key={i}
                        type="button"
                        onClick={() => onSlot(artist.id, slotDate.toISOString())}
                        disabled={conflict || !onShift}
                        onDragOver={(e) => {
                          if (!conflict && onShift) {
                            e.preventDefault();
                            e.dataTransfer.dropEffect = 'move';
                            e.currentTarget.classList.add('bg-mint/15');
                          }
                        }}
                        onDragLeave={(e) => e.currentTarget.classList.remove('bg-mint/15')}
                        onDrop={(e) => {
                          e.currentTarget.classList.remove('bg-mint/15');
                          const id = e.dataTransfer.getData('text/booking-id');
                          if (id) onMoveBooking(id, artist.id, slotDate.toISOString());
                        }}
                        className={clsx(
                          'absolute left-0 right-0 border-t border-transparent group',
                          conflict || !onShift
                            ? 'cursor-default'
                            : 'hover:bg-gold/5 cursor-pointer',
                          i % 2 === 0 ? 'border-t-gold-dim/20' : 'border-t-gold-dim/8',
                        )}
                        style={{
                          top: i * SLOT_HEIGHT_PX,
                          height: SLOT_HEIGHT_PX,
                        }}
                        data-testid={`slot-${artist.id}-${row}`}
                      >
                        {!conflict && onShift ? (
                          <Plus
                            size={10}
                            className="opacity-0 group-hover:opacity-60 text-gold-bright mx-auto"
                          />
                        ) : null}
                      </button>
                    );
                  })}
                  {mySuperseded.map((b) => {
                    // Ghost marker on the vacated slot: the original is kept for
                    // history (15 Jul). Rendered behind live tiles so a re-booked
                    // slot's real booking wins; click opens its full history.
                    const gStart = new Date(b.start);
                    const gMinutes = (gStart.getHours() - FIRST_HOUR) * 60 + gStart.getMinutes();
                    const gVis = Math.max(0, gMinutes);
                    if (gVis >= rows.length * SLOT_MIN) return null;
                    const last = b.rescheduleHistory?.[b.rescheduleHistory.length - 1];
                    const cust = customers[b.customerId];
                    const movedTo =
                      last && last.toIso !== last.fromIso ? simTimeFull(last.toIso) : 'no new date yet';
                    const ghostTip = `${cust ? customerName(cust) : 'Booking'} was here → moved to ${movedTo}${
                      last?.reason ? ` · ${RESCHEDULE_REASON_LABEL[last.reason]}` : ''
                    } · by ${last?.by ?? 'system'}${last?.note ? ` · "${last.note}"` : ''}`;
                    return (
                      <button
                        key={`sup-${b.id}`}
                        type="button"
                        title={ghostTip}
                        onClick={() => onBookingClick(b.id)}
                        className="absolute left-0 right-0 mx-0.5 flex items-center gap-1 rounded-md border border-dashed border-cream-faint/40 bg-cream-faint/5 px-2 text-cream-faint hover:border-gold-bright/50"
                        style={{ top: (gVis / SLOT_MIN) * SLOT_HEIGHT_PX, height: SLOT_HEIGHT_PX - 2 }}
                        data-testid={`superseded-${b.id}`}
                      >
                        <History size={10} />
                        <span className="text-[10px] uppercase tracking-widest line-through truncate">
                          {cust ? customerName(cust) : 'Moved'} · moved
                        </span>
                      </button>
                    );
                  })}
                  {laidDay.map(({ cell, lane }) => {
                    const laneWidthDay = 100 / totalLanesDay;
                    // Geometry is shared by both cell kinds: minutes from the 9am
                    // grid top, then the same left/width a single booking occupies.
                    const cellStart = new Date(cell.startMs);
                    const startMinutes =
                      (cellStart.getHours() - FIRST_HOUR) * 60 + cellStart.getMinutes();
                    const durationMin = (cell.endMs - cell.startMs) / 60_000;
                    // Clip to the visible 9-21 grid. A session that starts before
                    // opening is trimmed to the top edge (not clamped at full
                    // height, which would overshoot and stack on other early
                    // tiles); one that falls entirely outside the window is skipped.
                    const visStart = Math.max(0, startMinutes);
                    const visEnd = Math.min(rows.length * SLOT_MIN, startMinutes + durationMin);
                    if (visEnd <= visStart) return null;
                    const top = (visStart / SLOT_MIN) * SLOT_HEIGHT_PX;
                    const height = ((visEnd - visStart) / SLOT_MIN) * SLOT_HEIGHT_PX;
                    const left = `calc(${lane * laneWidthDay}% + 2px)`;
                    const width = `calc(${laneWidthDay}% - 4px)`;
                    if (cell.kind === 'stack') {
                      return (
                        <StackedHoldsTile
                          key={`stack-${cell.cluster.id}`}
                          id={cell.cluster.id}
                          holds={cell.cluster.holds}
                          deposits={deposits}
                          customers={customers}
                          clockIso={clockIso}
                          onOpen={onBookingClick}
                          style={{ top, height, left, width }}
                        />
                      );
                    }
                    const b = cell.booking;
                    const start = new Date(b.start);
                    const cust = customers[b.customerId];
                    const tone = bookingTileTone(b, deposits[b.depositId ?? '']);
                    const paid = tone.shade.level === 'full';
                    const price = bookingPriceAud(b, artist);
                    const lastResched = b.rescheduleHistory?.[b.rescheduleHistory.length - 1];
                    const tooltip = lastResched
                      ? `Rescheduled ${b.rescheduleHistory!.length}× · moved from ${simTimeFull(
                          lastResched.fromIso,
                        )} · ${RESCHEDULE_REASON_LABEL[lastResched.reason ?? 'other']} · by ${lastResched.by}`
                      : undefined;
                    return (
                      <button
                        key={b.id}
                        type="button"
                        draggable
                        title={tooltip}
                        onDragStart={(e) => {
                          e.dataTransfer.effectAllowed = 'move';
                          e.dataTransfer.setData('text/booking-id', b.id);
                          e.currentTarget.classList.add('opacity-50');
                        }}
                        onDragEnd={(e) => e.currentTarget.classList.remove('opacity-50')}
                        onClick={() => onBookingClick(b.id)}
                        style={{ top, height, left, width }}
                        className={clsx(
                          'absolute rounded-md px-2.5 py-1.5 text-left transition overflow-hidden shadow-ink-soft cursor-grab active:cursor-grabbing hover:brightness-110',
                          tone.className,
                          awaitingApprovalIds.has(b.id) && 'ring-1 ring-amber-400/60',
                          moveBookingId === b.id && 'ring-2 ring-gold-bright animate-pulse z-10',
                        )}
                        data-testid={`booking-${b.id}`}
                      >
                        <span className="font-inter text-sm block leading-tight truncate">
                          {cust ? customerName(cust) : b.notes ?? 'Booking'}
                        </span>
                        <span className="font-inter text-[11px] uppercase tracking-widest mt-0.5 flex items-center gap-1">
                          {lastResched ? (
                            <History size={10} className="shrink-0" aria-label="Rescheduled" />
                          ) : null}
                          <span className="truncate">
                            {format(start, 'HH:mm')}
                            {b.durationMin >= 60
                              ? ` · ${(b.durationMin / 60).toFixed(b.durationMin % 60 ? 1 : 0)}h`
                              : ` · ${b.durationMin}m`}
                            {' · '}
                            {aud(price)}
                          </span>
                        </span>
                        {height >= 90 ? (
                          <span className="font-serif text-[11px] mt-1 block line-clamp-2 opacity-80">
                            {b.designBrief}
                          </span>
                        ) : null}
                        {height >= 90 && b.referencePhotos.length > 0 ? (
                          <span className="mt-1 flex gap-1" data-testid={`booking-thumbs-${b.id}`}>
                            {b.referencePhotos.slice(0, 3).map((url, i) => (
                              <img
                                key={`${url}-${i}`}
                                src={url}
                                alt=""
                                loading="lazy"
                                className="h-6 w-6 object-cover rounded-sm border border-ink-black/20"
                              />
                            ))}
                          </span>
                        ) : null}
                        {paid ? (
                          <span className="absolute top-1.5 right-1.5">
                            <Wallet size={10} />
                          </span>
                        ) : null}
                        {awaitingApprovalIds.has(b.id) ? (
                          <span
                            className="absolute top-1 right-1 rounded-sm bg-amber-400 px-1 py-0.5 text-[8px] font-mono font-bold uppercase tracking-wide text-ink-black shadow-ink-soft"
                            data-testid={`booking-awaiting-${b.id}`}
                            title="Awaiting no-deposit approval"
                          >
                            Approval
                          </span>
                        ) : null}
                      </button>
                    );
                  })}
                  {off ? (
                    <div
                      data-testid={`day-off-tile-${artist.id}`}
                      title={off.note ?? dayOffTileTone(off.kind).label}
                      className={clsx(
                        'absolute inset-x-1 top-0 flex flex-col items-center justify-center rounded-md text-[11px]',
                        dayOffTileTone(off.kind).border,
                        dayOffTileTone(off.kind).fill,
                        dayOffTileTone(off.kind).text,
                      )}
                      style={{ height: rows.length * SLOT_HEIGHT_PX }}
                    >
                      <span className="font-semibold uppercase tracking-wide">
                        {dayOffTileTone(off.kind).label}
                      </span>
                      {off.note ? (
                        <span className="mt-0.5 px-2 text-center opacity-80 line-clamp-2">{off.note}</span>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

interface WeekViewProps {
  days: Date[];
  dayRev: (d: Date, artistId?: string) => number;
  artist: ArtistLike;
  rows: string[];
  bookingsForArtistOnDay: (
    artistId: string,
    day: Date,
  ) => Array<ReturnType<typeof useBrainStore.getState>['bookings'][string]>;
  deposits: ReturnType<typeof useBrainStore.getState>['deposits'];
  customers: ReturnType<typeof useBrainStore.getState>['customers'];
  clockIso: string;
  onBookingClick: (bookingId: string) => void;
  onAddOnDay: (day: Date) => void;
  awaitingApprovalIds: Set<string>;
}

/**
 * A single artist's week: 7 day-columns showing only `artist`'s bookings. One
 * artist can't be in two chairs at once, so tiles are full-width and readable;
 * the lane allocator only kicks in for the rare overlap (e.g. a collab). Days the
 * artist isn't working (RDO / leave / off-pattern) are shaded and badged.
 */
function WeekView({
  days,
  dayRev,
  artist,
  rows,
  bookingsForArtistOnDay,
  deposits,
  customers,
  clockIso,
  onBookingClick,
  onAddOnDay,
  awaitingApprovalIds,
}: WeekViewProps) {
  return (
    <div className="flex">
      <div className="w-14 shrink-0 pt-14">
        {rows.map((r) => (
          <div key={r} className="h-[30px] font-mono text-[10px] text-cream-soft text-right pr-2">
            {r.endsWith(':00') ? r : ''}
          </div>
        ))}
      </div>
      <div className="flex-1 grid grid-cols-7 gap-1">
        {days.map((day) => {
          const dayBookings = bookingsForArtistOnDay(artist.id, day)
            .slice()
            .sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());

          // Competing overbookable holds collapse into one stacked cell here too,
          // for parity with the day view; everything else lane-packs as before.
          const { clusters } = groupOverbookableHolds(dayBookings);
          const clusteredIds = new Set(clusters.flatMap((c) => c.holds.map((h) => h.id)));
          const cells: DayCell[] = [
            ...dayBookings
              .filter((b) => !clusteredIds.has(b.id))
              .map((booking) => {
                const startMs = new Date(booking.start).getTime();
                return {
                  kind: 'booking' as const,
                  startMs,
                  endMs: startMs + booking.durationMin * 60_000,
                  booking,
                };
              }),
            ...clusters.map((cluster) => ({
              kind: 'stack' as const,
              startMs: cluster.startMs,
              endMs: cluster.endMs,
              cluster,
            })),
          ];
          // Lane allocator: pack the rare overlapping cells into sub-columns.
          const laneEnds: number[] = [];
          const laid = cells
            .slice()
            .sort((a, b) => a.startMs - b.startMs)
            .map((cell) => {
              let lane = laneEnds.findIndex((endAt) => endAt <= cell.startMs);
              if (lane === -1) {
                laneEnds.push(cell.endMs);
                lane = laneEnds.length - 1;
              } else {
                laneEnds[lane] = cell.endMs;
              }
              return { cell, lane };
            });
          const totalLanes = Math.max(1, laneEnds.length);

          const off = dayOffFor(artist, day);
          const isOff = off !== null;

          return (
            <div
              key={day.toISOString()}
              className={clsx(
                'bg-ink-deep rounded-md border border-gold-dim/15',
                isOff && 'opacity-50',
              )}
            >
              <div className="flex items-center justify-between py-2 px-2 border-b border-gold-dim/15">
                <div>
                  <p className="font-inter text-sm text-cream">{format(day, 'EEE d')}</p>
                  {off ? (
                    <p className="font-inter text-[10px] text-blood uppercase tracking-widest mt-0.5">
                      {dayOffTileTone(off.kind).label}
                    </p>
                  ) : (
                    <p className="font-inter text-[10px] text-gold-dim uppercase mt-0.5">
                      {dayBookings.length} · {aud(dayRev(day, artist.id))}
                    </p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => onAddOnDay(day)}
                  disabled={isOff}
                  className={clsx(
                    'text-gold-bright hover:text-gold transition',
                    isOff && 'opacity-30 pointer-events-none',
                  )}
                  data-testid={`week-add-${format(day, 'yyyy-MM-dd')}`}
                  aria-label="Add booking"
                >
                  <Plus size={14} />
                </button>
              </div>
              <div className="grid grid-cols-1">
                <div className="relative overflow-hidden" style={{ height: `${rows.length * SLOT_HEIGHT_PX}px` }}>
                  {rows.map((_r, i) => (
                    <div
                      key={i}
                      className={clsx(
                        'border-t',
                        i === 0
                          ? 'border-transparent'
                          : i % 2 === 0
                            ? 'border-gold-dim/20'
                            : 'border-gold-dim/8',
                      )}
                      style={{ height: `${SLOT_HEIGHT_PX}px` }}
                    />
                  ))}
                  {laid.map(({ cell, lane }) => {
                    const laneWidth = 100 / totalLanes;
                    const cellStart = new Date(cell.startMs);
                    const startMinutes =
                      (cellStart.getHours() - FIRST_HOUR) * 60 + cellStart.getMinutes();
                    const durationMin = (cell.endMs - cell.startMs) / 60_000;
                    // Clip to the visible 9-21 grid (see DayView) so pre-open
                    // sessions trim to the top edge instead of stacking there.
                    const visStart = Math.max(0, startMinutes);
                    const visEnd = Math.min(rows.length * SLOT_MIN, startMinutes + durationMin);
                    if (visEnd <= visStart) return null;
                    const top = (visStart / SLOT_MIN) * SLOT_HEIGHT_PX;
                    const height = ((visEnd - visStart) / SLOT_MIN) * SLOT_HEIGHT_PX;
                    const left = `calc(${lane * laneWidth}% + 2px)`;
                    const width = `calc(${laneWidth}% - 4px)`;
                    if (cell.kind === 'stack') {
                      return (
                        <StackedHoldsTile
                          key={`stack-${cell.cluster.id}`}
                          id={cell.cluster.id}
                          holds={cell.cluster.holds}
                          deposits={deposits}
                          customers={customers}
                          clockIso={clockIso}
                          onOpen={onBookingClick}
                          style={{ top, height, left, width }}
                        />
                      );
                    }
                    const b = cell.booking;
                    const start = new Date(b.start);
                    const cust = customers[b.customerId];
                    const tone = bookingTileTone(b, deposits[b.depositId ?? '']);
                    return (
                      <button
                        key={b.id}
                        type="button"
                        onClick={() => onBookingClick(b.id)}
                        style={{ top, height, left, width }}
                        className={clsx(
                          'absolute rounded-sm px-1.5 py-1 text-left transition overflow-hidden hover:brightness-110',
                          tone.className,
                          awaitingApprovalIds.has(b.id) && 'ring-1 ring-amber-400/60',
                        )}
                        data-testid={`booking-${b.id}`}
                      >
                        <span className="font-inter text-[11px] block leading-tight truncate">
                          {cust ? customerName(cust) : 'Booking'}
                        </span>
                        <span className="font-inter text-[9px] uppercase tracking-widest block leading-tight truncate opacity-80">
                          {format(start, 'HH:mm')} · {sessionLabel(b.sessionLength)}
                        </span>
                      </button>
                    );
                  })}
                  {off ? (
                    <div
                      data-testid={`week-day-off-tile-${format(day, 'yyyy-MM-dd')}`}
                      title={off.note ?? dayOffTileTone(off.kind).label}
                      className={clsx(
                        'absolute inset-x-1 top-0 flex flex-col items-center justify-center rounded-md text-[11px]',
                        dayOffTileTone(off.kind).border,
                        dayOffTileTone(off.kind).fill,
                        dayOffTileTone(off.kind).text,
                      )}
                      style={{ height: rows.length * SLOT_HEIGHT_PX }}
                    >
                      <span className="font-semibold uppercase tracking-wide">
                        {dayOffTileTone(off.kind).label}
                      </span>
                      {off.note ? (
                        <span className="mt-0.5 px-2 text-center opacity-80 line-clamp-2">{off.note}</span>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
